import el, { createElementFromSafeContent } from '@finn-no/create-element';
import ConfirmDialog from '../lib/ConfirmDialog.js';
import { toast } from '@finn-no/common-browserbundle';

export default {
    dependencies: [
        '$element',
        'favoritesservice',
        'radio',
        'trackingservice',
        'loginservice',
        'templateservice',
    ],
    // eslint-disable-next-line max-params
    callable(
        element,
        favoritesService,
        radio,
        trackingservice,
        loginservice,
        templateservice
    ) {
        const heartAdIdAttribute = 'data-heart-ad-id';

        const isNewDesign = element.hasAttribute('data-new-design');
        const adIds = getAdIdsFromView();
        let favoriteDataKey = createFavoriteDataKey(adIds);
        let adFolderList = {};
        let favoriteDialog;
        let dialogContainer;
        let currentAdId;
        let nameInputEle;
        let form;

        setUpClickListeners();
        setUpSearchParameterListeners();

        const parameterFromSpidLoginRedirect = 'addFavorite';
        const url = new URL(document.baseURI);
        if (url.searchParams.has(parameterFromSpidLoginRedirect)) {
            currentAdId = url.searchParams.get(parameterFromSpidLoginRedirect);
            url.searchParams.delete(parameterFromSpidLoginRedirect);
            window.history.replaceState(null, null, url);
            const clickedHeart = element.querySelector(
                `[${heartAdIdAttribute}="${currentAdId}"]`
            );
            handleCreateFavoriteDialog(clickedHeart);
        }

        function createFavoriteDataKey(adIds) {
            const ids = adIds.slice(0);
            ids.sort();
            return ids.toString();
        }

        function loadFavoriteData(currentAdId) {
            const key = favoriteDataKey;
            if (adFolderList[currentAdId]) {
                return Promise.resolve({ key, data: adFolderList });
            } else {
                return favoritesService
                    .getFolderSearchResultItems(currentAdId)
                    .then(data => {
                        adFolderList = data;

                        // TODO: Just return `{ key, data }` and drop last `then`?
                        return adFolderList;
                    })
                    .then(data => ({ key, data }));
            }
        }

        function setUpClickListeners() {
            const searchResultHearts = [
                ...element.querySelectorAll('[data-show-favorite-list]'),
            ];
            searchResultHearts.forEach(heart => {
                heart.addEventListener('click', event => {
                    handleCreateFavoriteDialog(event.currentTarget);
                    event.preventDefault();
                    event.stopPropagation();
                });
            });
        }

        function setUpSearchParameterListeners() {
            radio('filter-and-search-changed').subscribe(() => {
                const key = createFavoriteDataKey(adIds);
                if (key !== favoriteDataKey) {
                    favoriteDataKey = key;
                    adFolderList = null;
                }
            });
        }

        function getAdIdsFromView() {
            const items = [...element.querySelectorAll('[data-finnkode]')];
            return items.map(item => item.getAttribute('data-finnkode'));
        }

        function handleCreateFavoriteDialog(clickedHeart) {
            const isLoggedIn = loginservice.isLoggedIn();
            currentAdId = clickedHeart.getAttribute(heartAdIdAttribute);
            dialogContainer = el('div');
            renderLoadingSpinner(dialogContainer);
            favoriteDialog = createDialog(dialogContainer, clickedHeart);
            favoriteDialog.show();

            if (isLoggedIn) {
                loadFavoriteData(currentAdId).then(({ key, data }) => {
                    if (key === favoriteDataKey) {
                        // Prevent out-of-order updates
                        dialogContainer.innerHTML = '';
                        renderNewListForm(dialogContainer);
                        if (isNewDesign) {
                            renderFavoriteListNewDesign(
                                dialogContainer,
                                data[currentAdId]
                            );
                        } else {
                            renderFavoriteList(
                                dialogContainer,
                                data[currentAdId]
                            );
                        }
                    }
                });
            } else {
                dialogContainer.innerHTML = renderLogin(currentAdId);
            }
        }

        function renderLogin(currentAdId) {
            const currentPageWithLoginParam = new URL(document.baseURI);
            currentPageWithLoginParam.searchParams.set(
                parameterFromSpidLoginRedirect,
                currentAdId
            );

            return templateservice.render('login_box', {
                currentPage: encodeURIComponent(currentPageWithLoginParam),
                loginMessage: 'Logg inn for å lagre annonsen',
            });
        }

        function renderLoadingSpinner(container) {
            const spinner = el(
                'div.flex.justify-center',
                null,
                el('div.spinner.mal')
            );
            container.appendChild(spinner);
        }

        function renderFavoriteList(container, folders) {
            const list = el(
                'ul.mvn',
                { 'data-favorite-list': '' },
                folders.map(f =>
                    el(
                        'li.pvm.list-line',
                        {
                            onclick() {
                                toggleFavorite(f);
                            },
                        },
                        createElementFromSafeContent(
                            'button.blank.fullwidth.t3.flex.align-items-center.justify-space-between.licorice',
                            { 'aria-label': f.folderName },
                            createElementFromSafeContent(
                                'span',
                                null,
                                f.folderName
                            ),
                            el(
                                'svg:svg.valign-middle.licorice',
                                {
                                    focusable: 'false',
                                    width: 34,
                                    height: 34,
                                },
                                el('svg:use', {
                                    'xlink:href': f.favorited
                                        ? '#icon-share-favorite-filled'
                                        : '#icon-share-favorite-empty',
                                })
                            )
                        ),
                        el('span', null)
                    )
                )
            );
            container.appendChild(list);
        }

        function renderFavoriteListNewDesign(container, folders) {
            const list = el(
                'ul.list',
                { 'data-favorite-list': '' },
                folders.map(f =>
                    el(
                        'li.flex.align-items-center.justify-space-between',
                        {
                            onclick() {
                                toggleFavorite(f);
                            },
                        },
                        createElementFromSafeContent(
                            'button.button.u-text-left.u-t3.u-truncate.u-size1of1.u-licorice',
                            { 'aria-label': f.folderName },
                            createElementFromSafeContent(
                                'span',
                                null,
                                f.folderName
                            )
                        ),
                        el(
                            `button.button.ghost-icon.ghost-icon--favorite${f.favorited
                                ? '.ghost-icon--is-toggled'
                                : ''}`,
                            null,
                            el(
                                'svg:svg',
                                {
                                    viewBox: '0 0 32 32',
                                    width: '32',
                                    height: '32',
                                },
                                el(
                                    'svg:g',
                                    {
                                        fill: 'none',
                                        'fill-rule': 'evenodd',
                                    },
                                    el('svg:path', {
                                        fill: 'currentColor',
                                        d: `M5.224 9.203a5.637 5.637 0 0 0 .974
                                            7.978l.146.109.413.309L8.233 18.7a106100.5 106100.5 0 0 1 7.765
                                            5.803 5101.342 5101.342 0 0 0
                                            7.77-5.806l1.475-1.103c.457-.343.457-.343.535-.4 2.515-1.943
                                            2.96-5.51 1-7.988-1.974-2.494-5.646-2.94-8.145-1.01l-2.638
                                            2.115-2.62-2.105c-2.534-1.94-6.182-1.494-8.15.996z`,
                                    }),
                                    el('svg:path', {
                                        fill: '#fff',
                                        'fill-rule': 'nonzero',
                                        d: `M5.224 9.203a5.637
                                            5.637 0 0 0 .974 7.978l.146.109.413.309L8.233 18.7a106100.5 106100.5
                                            0 0 1 7.765 5.803 5101.342 5101.342 0 0 0
                                            7.77-5.806l1.475-1.103c.457-.343.457-.343.535-.4 2.515-1.943
                                            2.96-5.51 1-7.988-1.974-2.494-5.646-2.94-8.145-1.01l-2.638
                                            2.115-2.62-2.105c-2.534-1.94-6.182-1.494-8.15.996zm9.386-2.57l1.386
                                            1.114 1.386-1.11c3.394-2.622 8.31-2.024 10.964 1.33C31 11.318 30.395
                                            16.156 27 18.776c0 0-7.33 5.486-11.004
                                            8.223-3.66-2.737-10.995-8.217-10.995-8.217a7.644 7.644 0 0
                                            1-1.345-10.82c2.654-3.357 7.556-3.948 10.954-1.33z`,
                                    })
                                )
                            )
                        )
                    )
                )
            );
            container.appendChild(list);
        }

        function renderNewListForm(container) {
            nameInputEle = el('input', {
                name: 'name',
                placeholder: 'Navn',
                'aria-label': 'Navn på ny liste',
                autocomplete: 'off',
            });
            form = el(
                'form.flex.align-items-end.mam.u-ma16',
                { action: '', 'data-create-list-form': '' },
                el(
                    'div.fullwidth.u-size1of1.input.input--text.u-mb0',
                    null,
                    nameInputEle
                ),
                el(
                    'div',
                    null,
                    el(
                        'button.mls.flex-no-shrink.no-break.button--default.u-no-break.u-ml8',
                        {
                            name: 'submit',
                        },
                        'Lag ny liste'
                    )
                )
            );
            form.addEventListener('submit', createList);
            container.appendChild(form);
        }

        function createDialog(container, openerEle) {
            return new ConfirmDialog(
                'Lagre favoritt',
                container,
                null,
                'Avbryt',
                {
                    onHideFocusElement:
                        openerEle.querySelector('[data-focus]') ||
                        element.querySelector('[data-focus]') ||
                        openerEle,
                }
            );
        }

        function toggleFavorite(folder) {
            favoritesService
                .toggleFavorite(folder.folderId, currentAdId, !folder.favorited)
                .then(favToggleResponse => {
                    folder.favorited = !folder.favorited;
                    showSuccessMessage(folder);
                    return favToggleResponse;
                })
                .then(favToggleResponse => {
                    if (isNewDesign) {
                        updateHeartNewDesign();
                    } else {
                        updateHeart();
                    }
                    favoriteDialog.hide();
                    return favToggleResponse;
                })
                .catch(favToggleResponse => {
                    toast({
                        level: `error`,
                        message: favToggleResponse,
                    });
                    favoriteDialog.hide();
                });
        }

        function showSuccessMessage(folder) {
            if (folder.favorited) {
                toast({ message: `Lagret i ${folder.folderName}` });
            } else {
                toast({ message: `Fjernet fra ${folder.folderName}` });
            }
        }

        function addFolderToListVar(folder) {
            adFolderList[currentAdId].push(folder);
        }

        function updateHeart() {
            const favorited = hasFavorites();
            const favoriteHeart = document.querySelector(
                `[${heartAdIdAttribute}="${currentAdId}"]`
            );
            if (favoriteHeart) {
                const useEle = favoriteHeart.querySelector('use');

                switch (useEle.href.baseVal) {
                    case '#icon-share-favorite-filled':
                        if (!favorited)
                            useEle.href.baseVal = '#icon-share-favorite-empty';
                        break;
                    case '#icon-share-favorite-empty':
                        if (favorited)
                            useEle.href.baseVal = '#icon-share-favorite-filled';
                        break;
                    default:
                        favoriteHeart.classList.toggle('is-toggled', favorited);
                }
            }
        }

        function updateHeartNewDesign() {
            const favorited = hasFavorites();
            const favoriteHeart = document.querySelector(
                `[${heartAdIdAttribute}="${currentAdId}"]`
            );
            if (favoriteHeart) {
                favoriteHeart.classList.toggle(
                    'ghost-icon--is-toggled',
                    favorited
                );
            }
        }

        function hasFavorites() {
            return adFolderList[currentAdId].some(folder => folder.favorited);
        }

        function createList(event) {
            event.preventDefault();

            const folderName = nameInputEle.value;
            const submitEle = form.querySelector('button');
            if (folderName.trim() === '') {
                return;
            }

            submitEle.classList.add('inprogress');
            nameInputEle.disabled = true;

            favoritesService
                .createList(folderName)
                .then(folderId => {
                    toggleFavorite({ folderName, folderId });
                    addFolderToListVar({
                        folderName,
                        folderId,
                        favorited: true,
                    });
                })
                .catch(errMsg => {
                    toast({ message: errMsg, level: 'error' });
                    nameInputEle.disabled = false;
                    nameInputEle.focus();
                })
                .then(() => {
                    nameInputEle.disabled = false;
                    submitEle.classList.remove('inprogress');
                });
        }
    },
};



// WEBPACK FOOTER //
// ./controllers/itemFavoriteToggle.js